import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

export interface DurationFieldsValue {
  years: string;
  months: string;
  days: string;
}

interface DurationFieldsProps {
  value: DurationFieldsValue;
  onChange: (next: DurationFieldsValue) => void;
  disabled?: boolean;
}

const NUMERIC_INPUT_CLASSNAME =
  "text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

/**
 * Ejar-style contract-duration entry: three numeric fields (years, months,
 * days), matching how EJAR displays a lease's remaining/total term (e.g.
 * ١٩ سنوات ٤ شهور ٢٧ أيام). Pure controlled component — the caller (
 * ContractInfoStep) owns the two-way sync with start_date/end_date.
 */
export function DurationFields({ value, onChange, disabled }: DurationFieldsProps) {
  const { t } = useLanguage();

  const handleFieldChange = (field: keyof DurationFieldsValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/[^0-9]/g, "");
    onChange({ ...value, [field]: sanitized });
  };

  return (
    <div className="grid grid-cols-3 gap-1">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{t("سنوات", "Years")}</Label>
        <Input
          data-testid="duration-years-input"
          type="text"
          inputMode="numeric"
          value={value.years}
          onChange={handleFieldChange("years")}
          placeholder="0"
          disabled={disabled}
          className={NUMERIC_INPUT_CLASSNAME}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{t("شهور", "Months")}</Label>
        <Input
          data-testid="duration-months-input"
          type="text"
          inputMode="numeric"
          value={value.months}
          onChange={handleFieldChange("months")}
          placeholder="0"
          disabled={disabled}
          className={NUMERIC_INPUT_CLASSNAME}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{t("أيام", "Days")}</Label>
        <Input
          data-testid="duration-days-input"
          type="text"
          inputMode="numeric"
          value={value.days}
          onChange={handleFieldChange("days")}
          placeholder="0"
          disabled={disabled}
          className={NUMERIC_INPUT_CLASSNAME}
        />
      </div>
    </div>
  );
}
